import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, MapPin, Calendar, Upload, FileText, IdCard, ShieldCheck, ShieldAlert, Heart, Home, Briefcase, Stethoscope, Accessibility, Award, CreditCard, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import { JOB_PREFERENCES } from "@/constants";
import { useJobPreferences } from "@/hooks/useJobPreferences";
import DateInputFR from "@/components/DateInputFR";
import NationalitySelect from "@/components/NationalitySelect";
import CityPostalAutocomplete from "@/components/CityPostalAutocomplete";
import SsnInput, { validateSsn } from "@/components/SsnInput";
import PhoneInputFr from "@/components/PhoneInputFr";
import { isValidNationality } from "@/lib/nationalities";
import { formatPhoneFr, upperLastName, validatePhoneFr } from "@/lib/formatters";
import axios from "axios";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.4 } }),
};

export default function AuthPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next") || "";
  const completeMode = searchParams.get("complete") === "1";
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState("auth");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetForm, setResetForm] = useState({ token: "", password: "" });

  const postLoginRedirect = (u) => {
    // Priorité : ?next=... (uniquement chemins internes)
    if (nextParam && nextParam.startsWith("/")) {
      // Admin ne peut pas aller dans /app (mobile candidate)
      if (u?.role === "admin" && nextParam.startsWith("/app")) {
        navigate("/admin", { replace: true });
        return;
      }
      navigate(nextParam, { replace: true });
      return;
    }
    navigate(u?.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  };

  // If already authenticated:
  //   - completeMode + profile_complete === false → render completion form (below)
  //   - otherwise → redirect
  if (user && !(completeMode && user.profile_complete === false)) {
    postLoginRedirect(user);
    return null;
  }

  // OAuth registration completion (Google/Facebook/Apple) — user signed in but
  // required registration fields are missing → force full form completion.
  if (user && completeMode && user.profile_complete === false) {
    return (
      <main data-testid="auth-page-complete" className="min-h-screen bg-[#FAFAFA] py-8 px-4">
        <motion.div initial="hidden" animate="visible" className="w-full max-w-3xl mx-auto">
          <motion.div variants={fadeUp} custom={0} className="text-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_c50239db-e303-4ac2-9fad-ed65c9848429/artifacts/l9apbe3g_LOGO-FR-INTERIM.png" alt="FR INTERIM" className="h-14 w-auto mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#3F3F3F]">Complétez votre inscription</h1>
            <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
              Vous vous êtes connecté avec {user.auth_type === "google" ? "Google" : user.auth_type === "facebook" ? "Facebook" : user.auth_type === "apple" ? "Apple" : "un fournisseur d'identité externe"}.
              Pour finaliser votre inscription et accéder aux offres, merci de renseigner les informations obligatoires ci-dessous.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} custom={1} className="bg-white border border-gray-200 p-6 sm:p-8 shadow-sm">
            <RegisterForm
              setUser={setUser}
              onSuccess={() => navigate("/dashboard", { replace: true })}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              completeMode
              prefilled={user}
            />
          </motion.div>
        </motion.div>
      </main>
    );
  }

  if (view === "forgot") {
    return (
      <main data-testid="auth-page" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4">
        <motion.div initial="hidden" animate="visible" className="w-full max-w-md">
          <motion.div variants={fadeUp} custom={0} className="text-center mb-8"><img src="https://customer-assets.emergentagent.com/job_c50239db-e303-4ac2-9fad-ed65c9848429/artifacts/l9apbe3g_LOGO-FR-INTERIM.png" alt="FR INTERIM" className="h-16 w-auto mx-auto mb-4" /></motion.div>
          <motion.div variants={fadeUp} custom={1} className="bg-white border border-gray-200 p-8 shadow-sm">
            <button onClick={() => setView("auth")} className="flex items-center gap-1 text-sm text-[#662483] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</button>
            <h2 className="text-lg font-bold text-[#3F3F3F] mb-2">Mot de passe oublié</h2>
            <p className="text-sm text-gray-500 mb-4">Entrez votre email pour recevoir un code de réinitialisation.</p>
            <div className="space-y-4">
              <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="votre@email.com" className="rounded-none" data-testid="forgot-email" />
              <Button className="w-full bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="forgot-submit" onClick={async () => {
                if (!forgotEmail) { toast.error("Entrez votre email"); return; }
                try { await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail }); toast.success("Si un compte existe, un email avec un code a été envoyé"); setView("reset"); } catch { toast.error("Erreur"); }
              }}>Envoyer le code</Button>
            </div>
          </motion.div>
        </motion.div>
      </main>
    );
  }

  if (view === "reset") {
    return (
      <main data-testid="auth-page" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4">
        <motion.div initial="hidden" animate="visible" className="w-full max-w-md">
          <motion.div variants={fadeUp} custom={0} className="text-center mb-8"><img src="https://customer-assets.emergentagent.com/job_c50239db-e303-4ac2-9fad-ed65c9848429/artifacts/l9apbe3g_LOGO-FR-INTERIM.png" alt="FR INTERIM" className="h-16 w-auto mx-auto mb-4" /></motion.div>
          <motion.div variants={fadeUp} custom={1} className="bg-white border border-gray-200 p-8 shadow-sm">
            <button onClick={() => setView("forgot")} className="flex items-center gap-1 text-sm text-[#662483] mb-4"><ArrowLeft className="w-4 h-4" /> Retour</button>
            <h2 className="text-lg font-bold text-[#3F3F3F] mb-2">Réinitialiser le mot de passe</h2>
            <div className="space-y-4">
              <div><Label className="text-sm font-semibold mb-1 block">Code reçu par email</Label><Input value={resetForm.token} onChange={(e) => setResetForm({ ...resetForm, token: e.target.value })} placeholder="Code à 8 caractères" className="rounded-none uppercase tracking-widest text-center font-bold" data-testid="reset-token" /></div>
              <div><Label className="text-sm font-semibold mb-1 block">Nouveau mot de passe</Label><Input type="password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} placeholder="Min. 6 caractères" className="rounded-none" data-testid="reset-password" /></div>
              <Button className="w-full bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="reset-submit" onClick={async () => {
                if (!resetForm.token || !resetForm.password) { toast.error("Remplissez tous les champs"); return; }
                try { await axios.post(`${API}/auth/reset-password`, resetForm); toast.success("Mot de passe réinitialisé ! Connectez-vous."); setView("auth"); } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
              }}>Réinitialiser</Button>
            </div>
          </motion.div>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="auth-page" className="min-h-screen bg-[#FAFAFA] flex items-center justify-center py-12 px-4">
      <motion.div initial="hidden" animate="visible" className="w-full max-w-md">
        <motion.div variants={fadeUp} custom={0} className="text-center mb-8">
          <img src="https://customer-assets.emergentagent.com/job_c50239db-e303-4ac2-9fad-ed65c9848429/artifacts/l9apbe3g_LOGO-FR-INTERIM.png" alt="FR INTERIM" className="h-16 w-auto mx-auto mb-4" />
          <p className="text-sm text-gray-500">Votre espace personnel FR INTERIM</p>
        </motion.div>
        <motion.div variants={fadeUp} custom={1} className="bg-white border border-gray-200 p-8 shadow-sm">
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6 rounded-none bg-[#FAFAFA]">
              <TabsTrigger value="login" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#662483] font-semibold" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#662483] font-semibold" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>
            <TabsContent value="login"><LoginForm setUser={setUser} onSuccess={postLoginRedirect} showPassword={showPassword} setShowPassword={setShowPassword} onForgot={() => setView("forgot")} /></TabsContent>
            <TabsContent value="register"><RegisterForm setUser={setUser} onSuccess={postLoginRedirect} showPassword={showPassword} setShowPassword={setShowPassword} /></TabsContent>
          </Tabs>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500 mb-4">Ou connectez-vous avec</p>
            <div className="space-y-3">
              <Button variant="outline" className="w-full rounded-none border-gray-300 text-[#3F3F3F] font-medium" data-testid="google-login-btn"
                onClick={() => { const redirectUrl = window.location.origin + '/dashboard'; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`; }}>
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continuer avec Google
              </Button>
              <Button variant="outline" className="w-full rounded-none border-gray-300 text-[#3F3F3F] font-medium" onClick={() => toast.info("Connexion Facebook bientôt disponible")} data-testid="facebook-login-btn">
                <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Continuer avec Facebook
              </Button>
              <Button variant="outline" className="w-full rounded-none border-gray-300 text-[#3F3F3F] font-medium" onClick={() => toast.info("Connexion Apple bientôt disponible")} data-testid="apple-login-btn">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>
                Continuer avec Apple
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </main>
  );
}

function LoginForm({ setUser, onSuccess, showPassword, setShowPassword, onForgot }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Veuillez remplir tous les champs"); return; }
    setLoading(true);
    try { const res = await axios.post(`${API}/auth/login`, form, { withCredentials: true }); setUser(res.data); toast.success("Connexion réussie !"); onSuccess(res.data); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur de connexion"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
      <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-gray-300 pl-10" placeholder="votre@email.com" data-testid="login-email" /></div></div>
      <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Mot de passe</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input type={showPassword ? "text" : "password"} autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none border-gray-300 pl-10 pr-10" placeholder="Votre mot de passe" data-testid="login-password" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
      <button type="button" onClick={onForgot} className="text-xs text-[#662483] hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</button>
      <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold py-3 rounded-none hover:opacity-90" data-testid="login-submit">{loading ? "Connexion..." : "Se connecter"}</Button>
    </form>
  );
}

function UploadZone({ testid, icon: Icon, label, hint, accept, file, onChange }) {
  const inputId = `${testid}-input`;
  return (
    <label htmlFor={inputId} className="border border-dashed border-gray-300 hover:border-[#662483] bg-[#FAFAFA] p-3 cursor-pointer flex items-center gap-3 transition-colors" data-testid={`${testid}-zone`}>
      <Icon className="w-5 h-5 text-[#662483] shrink-0" />
      <div className="flex-1 min-w-0">
        {file ? (
          <>
            <p className="text-sm font-semibold text-[#3F3F3F] truncate">{file.name}</p>
            <p className="text-[11px] text-gray-500">{(file.size / 1024).toFixed(0)} Ko — cliquez pour remplacer</p>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold text-[#3F3F3F]">{label}</p>
            <p className="text-[11px] text-gray-500">{hint}</p>
          </>
        )}
      </div>
      {file && <Upload className="w-4 h-4 text-green-600 shrink-0" />}
      <input
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
    </label>
  );
}

function RegisterForm({ setUser, onSuccess, showPassword, setShowPassword, completeMode = false, prefilled = null }) {
  const navigate = useNavigate();
  const { prefs: dynamicPrefs } = useJobPreferences();
  const JOB_PREFS = dynamicPrefs.length ? dynamicPrefs : JOB_PREFERENCES;
  const initialForm = {
    first_name: prefilled?.first_name || (prefilled?.name?.split(" ")[0] || "") || "",
    last_name: prefilled?.last_name || (prefilled?.name?.split(" ").slice(1).join(" ") || "") || "",
    email: prefilled?.email || "",
    password: "",
    phone: prefilled?.phone || "",
    address: prefilled?.address || "",
    postal_code: prefilled?.postal_code || "",
    city: prefilled?.city || "",
    subscribed_offers: prefilled?.subscribed_offers ?? true,
    preferences: prefilled?.preferences || [],
    mobility_km: prefilled?.mobility_km || 30,
    disponibilite_debut: prefilled?.disponibilite_debut || "",
    disponibilite_fin: prefilled?.disponibilite_fin || "",
    social_security_number: prefilled?.social_security_number || "",
    rgpd_consent: prefilled?.rgpd_consent || false,
    civility: prefilled?.civility || "",
    nationality: prefilled?.nationality || "",
    birth_place: prefilled?.birth_place || "",
    birth_name: prefilled?.birth_name || "",
    birth_date: prefilled?.birth_date || "",
    marital_status: prefilled?.marital_status || "",
    id_doc_type: prefilled?.id_doc_type || "",
    id_doc_validity_start: prefilled?.id_doc_validity_start || "",
    id_doc_validity_end: prefilled?.id_doc_validity_end || "",
    id_doc_origin: prefilled?.id_doc_origin || "",
    id_doc_department: prefilled?.id_doc_department || "",
    id_doc_issuer: prefilled?.id_doc_issuer || "",
    id_doc_issuer_code: prefilled?.id_doc_issuer_code || "",
    id_doc_number: prefilled?.id_doc_number || "",
    iban: prefilled?.iban || "",
    bic: prefilled?.bic || "",
    bank_domiciliation: prefilled?.bank_domiciliation || "",
    sms_operational_consent: prefilled?.sms_operational_consent ?? true,
    sms_marketing_consent: prefilled?.sms_marketing_consent ?? true,
    emergency_contact_name: prefilled?.emergency_contact_name || "",
    emergency_contact_phone: prefilled?.emergency_contact_phone || "",
    emergency_contact_relation: prefilled?.emergency_contact_relation || "",
  };
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({ cv: null, id_card_recto: null, id_card_verso: null, attestation_secu: null, justificatif_domicile: null, certificat_travail: null, visite_medicale: null, rqth: null, caces_habilitations: null, rib: null, other: null });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // becomes true after first failed submit -> enables visual error styling

  const setFile = (key, f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 5 Mo)"); return; }
    setFiles((prev) => ({ ...prev, [key]: f }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Tous les champs sont obligatoires sauf disponibilite_fin
    const missing = [];
    if (!form.civility) missing.push("civilité");
    if (!form.first_name) missing.push("prénom");
    if (!form.last_name) missing.push("nom");
    if (!form.email) missing.push("email");
    if (!completeMode && !form.password) missing.push("mot de passe");
    if (!form.phone) missing.push("téléphone");
    if (form.phone) {
      const pv = validatePhoneFr(form.phone);
      if (!pv.valid) { setSubmitted(true); toast.error(pv.error || "Numéro de téléphone invalide"); return; }
    }
    if (form.emergency_contact_phone) {
      const epv = validatePhoneFr(form.emergency_contact_phone);
      if (!epv.valid) { setSubmitted(true); toast.error("Contact d'urgence : " + (epv.error || "numéro invalide")); return; }
    }
    if (!form.address) missing.push("adresse");
    if (!form.postal_code) missing.push("code postal");
    if (!form.city) missing.push("ville");
    if (!form.nationality) missing.push("nationalité");
    if (form.nationality && !isValidNationality(form.nationality)) {
      setSubmitted(true);
      toast.error("La nationalité saisie n'est pas valide. Sélectionnez une entrée dans la liste.");
      return;
    }
    if (!form.birth_date) missing.push("date de naissance");
    if (!form.birth_place) missing.push("lieu de naissance");
    if (!form.marital_status) missing.push("situation familiale");
    if (!form.social_security_number) missing.push("n° de sécurité sociale");
    if (!form.disponibilite_debut) missing.push("date de début de disponibilité");
    if (!form.id_doc_type) missing.push("type de pièce d'identité");
    if (!form.id_doc_validity_start) missing.push("date de début de validité de la pièce");
    if (!form.id_doc_validity_end) missing.push("date d'expiration de la pièce");
    if (!form.id_doc_origin) missing.push("origine de la pièce");
    if (!form.id_doc_number) missing.push("numéro de pièce");
    if (!form.id_doc_department) missing.push("département de délivrance");
    if (!form.id_doc_issuer_code) missing.push("organisme émetteur");
    if (!form.id_doc_issuer) missing.push("département et lieu d'émission");
    // Both faces of the ID document (or residence permit) are required.
    if (!files?.id_card_recto) missing.push("scan pièce d'identité (recto)");
    if (!files?.id_card_verso) missing.push("scan pièce d'identité (verso)");
    if (missing.length > 0) { setSubmitted(true); toast.error("Champs obligatoires manquants : " + missing.join(", ")); return; }
    // Structural check of the SSN (15 digits + prefix ↔ civility + key)
    // Utilise la même logique que <SsnInput> pour rester cohérent avec le feedback inline.
    const ssn = form.social_security_number.replace(/\D/g, "");
    if (ssn.length !== 15) { setSubmitted(true); toast.error("Le numéro de sécurité sociale doit contenir 15 chiffres."); return; }
    const chk = validateSsn(ssn, form.civility, form.birth_date);
    if (chk.ok === false) {
      setSubmitted(true);
      toast.error(chk.reason || "Numéro de sécurité sociale invalide");
      return;
    }
    if (!completeMode && form.password.length < 6) { setSubmitted(true); toast.error("Le mot de passe doit contenir au moins 6 caractères"); return; }
    if (!form.rgpd_consent) { setSubmitted(true); toast.error("Vous devez accepter le traitement de vos données personnelles pour vous inscrire"); return; }
    setLoading(true);
    try {
      let res;
      if (completeMode) {
        // OAuth completion — update existing account profile
        const { password, email, ...profilePayload } = form;
        res = await axios.put(`${API}/auth/profile`, profilePayload, { withCredentials: true });
      } else {
        res = await axios.post(`${API}/auth/register`, form, { withCredentials: true });
      }
      setUser(res.data);
      // Upload selected files (sequential to keep order/logs predictable).
      // The "id_card_recto" / "id_card_verso" zones are remapped to
      // "carte_sejour_recto" / "carte_sejour_verso" when the user selected
      // a carte de séjour. All other doc_types pass through unchanged.
      const isSejour = form.id_doc_type === "carte_sejour";
      const docsToUpload = Object.entries(files)
        .filter(([, f]) => !!f)
        .map(([key, file]) => {
          let docType = key;
          if (isSejour && key === "id_card_recto") docType = "carte_sejour_recto";
          else if (isSejour && key === "id_card_verso") docType = "carte_sejour_verso";
          return [docType, file];
        });
      let uploadedCount = 0;
      let failedCount = 0;
      for (const [doc_type, file] of docsToUpload) {
        try {
          const fd = new FormData();
          fd.append("doc_type", doc_type);
          fd.append("file", file);
          await axios.post(`${API}/documents/upload`, fd, { withCredentials: true });
          uploadedCount += 1;
        } catch {
          failedCount += 1;
        }
      }
      if (uploadedCount > 0 && failedCount === 0) {
        toast.success(`Inscription réussie ! ${uploadedCount} document${uploadedCount > 1 ? "s" : ""} déposé${uploadedCount > 1 ? "s" : ""}.`);
      } else if (uploadedCount > 0 && failedCount > 0) {
        toast.warning(`Inscription réussie. ${uploadedCount} document(s) déposé(s), ${failedCount} échec(s) — relancez l'upload depuis votre espace.`);
      } else if (failedCount > 0) {
        toast.warning("Inscription réussie, mais les documents n'ont pas pu être déposés. Vous pourrez les déposer depuis votre espace.");
      } else {
        toast.success("Inscription réussie ! Bienvenue chez FR INTERIM");
      }
      navigate("/dashboard", { replace: true });
      // Note: after registration, redirect always to /dashboard (initial docs upload flow).
      // The mobile app /app is reachable from there via bottom nav or PWA install.
    }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'inscription"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block">Civilité *</Label>
        <Select value={form.civility} onValueChange={(v) => setForm({ ...form, civility: v })}>
          <SelectTrigger className={`rounded-none ${submitted && !form.civility ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-civility">
            <SelectValue placeholder="Sélectionner..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monsieur">Monsieur</SelectItem>
            <SelectItem value="madame">Madame</SelectItem>
            <SelectItem value="mademoiselle">Mademoiselle</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Prénom *</Label><Input autoComplete="given-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-none border-gray-300" placeholder="Jean" data-testid="register-first-name" /></div>
        <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Nom *</Label><Input autoComplete="family-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: upperLastName(e.target.value) })} className="rounded-none border-gray-300" placeholder="DUPONT" data-testid="register-last-name" /></div>
      </div>
      <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Email *</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input type="email" autoComplete="email" value={form.email} onChange={(e) => !completeMode && setForm({ ...form, email: e.target.value })} readOnly={completeMode} className={`rounded-none border-gray-300 pl-10 ${completeMode ? "bg-gray-100 text-gray-500" : ""}`} placeholder="votre@email.com" data-testid="register-email" /></div>{completeMode && <p className="text-[10px] text-gray-500 mt-1">Email lié à votre compte {form.email && form.email.includes("@") ? form.email.split("@")[1] : ""} — non modifiable.</p>}</div>
      <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Téléphone *</Label><PhoneInputFr value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} withIcon required data-testid="register-phone" /></div>
      <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Adresse *</Label><Input autoComplete="street-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none border-gray-300" placeholder="12 rue de la Logistique" data-testid="register-address" /></div>
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block">Localisation *</Label>
        <CityPostalAutocomplete
          postalCode={form.postal_code}
          city={form.city}
          onChange={({ postal_code, city }) => setForm({ ...form, postal_code, city })}
          required
          testidPrefix="register-residence"
          error={submitted && (!form.postal_code || !form.city)}
          labels={{ postal: "Code postal", city: "Ville" }}
        />
      </div>
      {!completeMode && (
        <div><Label className="text-sm font-semibold text-gray-700 mb-2 block">Mot de passe *</Label><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input type={showPassword ? "text" : "password"} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none border-gray-300 pl-10 pr-10" placeholder="Min. 6 caractères" data-testid="register-password" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
      )}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block">État civil</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Nationalité *</Label>
            <NationalitySelect
              value={form.nationality}
              onChange={(v) => setForm({ ...form, nationality: v })}
              required
              dataTestId="register-nationality"
              placeholder="ex. Française"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Date de naissance *</Label>
            <DateInputFR value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} className="rounded-none border-gray-300" testid="register-birth-date" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Lieu de naissance *</Label>
            <Input value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} placeholder="Ville, Pays" className="rounded-none border-gray-300" data-testid="register-birth-place" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Nom de naissance</Label>
            <Input value={form.birth_name} onChange={(e) => setForm({ ...form, birth_name: e.target.value })} placeholder="Si différent du nom actuel" className="rounded-none border-gray-300" data-testid="register-birth-name" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-gray-500 mb-1 block">Situation familiale *</Label>
            <Select value={form.marital_status} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
              <SelectTrigger className={`rounded-none ${submitted && !form.marital_status ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-marital-status">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="celibataire">Célibataire</SelectItem>
                <SelectItem value="marie">Marié(e)</SelectItem>
                <SelectItem value="pacse">Pacsé(e)</SelectItem>
                <SelectItem value="divorce">Divorcé(e)</SelectItem>
                <SelectItem value="veuf">Veuf / Veuve</SelectItem>
                <SelectItem value="concubinage">En concubinage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block flex items-center gap-2"><MapPin className="w-4 h-4" /> Mobilité géographique</Label>
        <div className="flex items-center gap-3">
          <Input type="number" min="5" max="200" value={form.mobility_km} onChange={(e) => setForm({ ...form, mobility_km: parseInt(e.target.value) || 30 })} className="rounded-none border-gray-300 w-24" data-testid="register-mobility" />
          <span className="text-sm text-gray-500">km autour de mon adresse</span>
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block">Préférences de recherche</Label>
        <div className="grid grid-cols-2 gap-2">
          {JOB_PREFS.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
              <Checkbox checked={form.preferences.includes(key)} onCheckedChange={(checked) => setForm(prev => ({ ...prev, preferences: checked ? [...prev.preferences, key] : prev.preferences.filter(p => p !== key) }))} data-testid={`register-pref-${key}`} />
              <label className="text-xs text-gray-600">{label}</label>
            </div>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block flex items-center gap-2"><Calendar className="w-4 h-4" /> Disponibilité *</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input type="date" value={form.disponibilite_debut} onChange={(e) => setForm({ ...form, disponibilite_debut: e.target.value })} className="rounded-none border-gray-300 hidden" data-testid="register-dispo-debut-hidden" />
            <DateInputFR value={form.disponibilite_debut} onChange={(v) => setForm({ ...form, disponibilite_debut: v })} className="rounded-none border-gray-300" testid="register-dispo-debut" />
            <p className="text-[10px] text-gray-400 mt-1">Début *</p>
          </div>
          <div>
            <Input type="date" value={form.disponibilite_fin} onChange={(e) => setForm({ ...form, disponibilite_fin: e.target.value })} className="rounded-none border-gray-300 hidden" data-testid="register-dispo-fin-hidden" />
            <DateInputFR value={form.disponibilite_fin} onChange={(v) => setForm({ ...form, disponibilite_fin: v })} className="rounded-none border-gray-300" testid="register-dispo-fin" />
            <p className="text-[10px] text-gray-400 mt-1">Fin</p>
          </div>
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-2 block flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Numéro de sécurité sociale *</Label>
        <SsnInput
          value={form.social_security_number}
          onChange={(digits) => setForm({ ...form, social_security_number: digits })}
          civility={form.civility}
          birthDateIso={form.birth_date}
          required
          testid="register-ssn"
        />
      </div>
      {/* Détails de la pièce d'identité (formulaire, avant la section Pièces à joindre) */}
      <div className="bg-[#FAFAFA] border border-gray-200 p-4 space-y-3" data-testid="register-id-details-block">
        <p className="text-xs font-semibold text-[#662483] flex items-center gap-2"><IdCard className="w-3.5 h-3.5" /> Détails de la pièce d&apos;identité</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs text-gray-500 mb-1 block">Type de pièce *</Label>
            <Select value={form.id_doc_type} onValueChange={(v) => setForm({ ...form, id_doc_type: v })}>
              <SelectTrigger className={`rounded-none bg-white ${submitted && !form.id_doc_type ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-doc-type">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cni">Carte Nationale d&apos;Identité</SelectItem>
                <SelectItem value="passeport">Passeport</SelectItem>
                <SelectItem value="carte_sejour">Carte de séjour</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Valide du *</Label>
            <Input type="date" value={form.id_doc_validity_start} onChange={(e) => setForm({ ...form, id_doc_validity_start: e.target.value })} className={`rounded-none bg-white ${submitted && !form.id_doc_validity_start ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-validity-start" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Valide jusqu&apos;au *</Label>
            <DateInputFR value={form.id_doc_validity_end} onChange={(v) => setForm({ ...form, id_doc_validity_end: v })} className={`rounded-none bg-white ${submitted && !form.id_doc_validity_end ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} testid="register-id-validity-end" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Origine *</Label>
            <Input value={form.id_doc_origin} onChange={(e) => setForm({ ...form, id_doc_origin: e.target.value })} placeholder="ex. France" className={`rounded-none bg-white ${submitted && !form.id_doc_origin ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-origin" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Numéro de pièce *</Label>
            <Input value={form.id_doc_number} onChange={(e) => setForm({ ...form, id_doc_number: e.target.value })} placeholder="ex. YJ84LQDMF" className={`rounded-none bg-white ${submitted && !form.id_doc_number ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-number" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Département *</Label>
            <Input value={form.id_doc_department} onChange={(e) => setForm({ ...form, id_doc_department: e.target.value })} placeholder="ex. 75 ou Paris" className={`rounded-none bg-white ${submitted && !form.id_doc_department ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-department" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Organisme émetteur *</Label>
            <select value={form.id_doc_issuer_code} onChange={(e) => setForm({ ...form, id_doc_issuer_code: e.target.value })} className={`w-full h-10 rounded-none border bg-white px-3 text-sm ${submitted && !form.id_doc_issuer_code ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-issuer-code">
              <option value="">—</option>
              <option value="PP">PP — Préfecture de Police (Paris)</option>
              <option value="PR">PR — Préfecture</option>
              <option value="SP">SP — Sous-préfecture</option>
              <option value="MA">MA — Mairie</option>
              <option value="CO">CO — Commissariat</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-gray-500 mb-1 block">Département et lieu *</Label>
            <Input value={form.id_doc_issuer} onChange={(e) => setForm({ ...form, id_doc_issuer: e.target.value })} placeholder="ex. Pref Isère" className={`rounded-none bg-white ${submitted && !form.id_doc_issuer ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`} data-testid="register-id-issuer" />
          </div>
        </div>
      </div>

      {/* Coordonnées bancaires (optionnel à l'inscription) */}
      <div className="border border-gray-200 p-3 bg-gray-50" data-testid="register-bank-block">
        <Label className="text-xs font-bold text-[#662483] mb-2 block uppercase tracking-wider">Coordonnées bancaires (facultatif)</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs text-gray-500 mb-1 block">IBAN</Label>
            <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} placeholder="FR76 1234 5678 9012 3456 7890 123" className="rounded-none border-gray-300 bg-white font-mono" data-testid="register-iban" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">BIC</Label>
            <Input value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })} placeholder="BNPAFRPPXXX" className="rounded-none border-gray-300 bg-white font-mono" data-testid="register-bic" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Domiciliation</Label>
            <Input value={form.bank_domiciliation} onChange={(e) => setForm({ ...form, bank_domiciliation: e.target.value })} placeholder="ex. BNP Paribas Paris 9e" className="rounded-none border-gray-300 bg-white" data-testid="register-bank-dom" />
          </div>
        </div>
      </div>

      {/* SECTION UNIQUE "PIÈCES À JOINDRE" */}
      <div className="border-2 border-[#662483]/30 bg-white" data-testid="register-uploads-section">
        <div className="bg-gradient-to-r from-[#F0E9F3] to-[#E6F0F7] px-4 py-3 border-b border-[#662483]/30 flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[#662483]" />
          <h3 className="text-sm font-bold text-[#662483] uppercase tracking-wider">Pièces à joindre</h3>
        </div>
        <div className="p-4 space-y-5">
          <p className="text-[11px] text-gray-500">
            Certaines pièces sont obligatoires (marquées d&apos;un <span className="text-red-500 font-bold">*</span>), les autres sont facultatives. Vous pourrez compléter votre dossier plus tard depuis votre espace personnel.
          </p>

          {/* Sous-groupe 1 : Identité (obligatoire) */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#3F3F3F]">
              <IdCard className="w-3 h-3 text-[#662483]" /> Pièce d&apos;identité <span className="text-red-500">*</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <UploadZone
                testid="register-id-recto"
                icon={IdCard}
                label={form.id_doc_type === "carte_sejour" ? "Carte de séjour — recto *" : "Pièce d'identité — recto *"}
                hint={form.id_doc_type === "passeport" ? "Passeport (page photo) — PDF, JPG, PNG" : "Côté avec la photo — PDF, JPG, PNG"}
                accept=".pdf,.jpg,.jpeg,.png"
                file={files.id_card_recto}
                onChange={(f) => setFile("id_card_recto", f)}
              />
              {form.id_doc_type !== "passeport" && (
                <UploadZone
                  testid="register-id-verso"
                  icon={IdCard}
                  label={form.id_doc_type === "carte_sejour" ? "Carte de séjour — verso *" : "Pièce d'identité — verso *"}
                  hint="Côté avec l'adresse / signature — PDF, JPG, PNG"
                  accept=".pdf,.jpg,.jpeg,.png"
                  file={files.id_card_verso}
                  onChange={(f) => setFile("id_card_verso", f)}
                />
              )}
            </div>
          </div>

          {/* Sous-groupe 2 : Dossier candidat */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#3F3F3F]">
              <FileText className="w-3 h-3 text-[#662483]" /> Dossier candidat
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <UploadZone testid="register-cv" icon={FileText} label="CV" hint="PDF, DOC, DOCX, JPG, PNG – prise photo mobile OK" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" file={files.cv} onChange={(f) => setFile("cv", f)} />
              <UploadZone testid="register-secu" icon={Heart} label="Attestation Sécu / Carte vitale" hint="PDF, JPG, PNG – max 5 Mo" accept=".pdf,.jpg,.jpeg,.png" file={files.attestation_secu} onChange={(f) => setFile("attestation_secu", f)} />
              <UploadZone testid="register-justif-domicile" icon={Home} label="Justificatif de domicile" hint="Facture EDF, quittance… (moins de 3 mois) — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.justificatif_domicile} onChange={(f) => setFile("justificatif_domicile", f)} />
              <UploadZone testid="register-certif-travail" icon={Briefcase} label="Certificat de travail / bulletins" hint="PDF, JPG, PNG – max 5 Mo" accept=".pdf,.jpg,.jpeg,.png" file={files.certificat_travail} onChange={(f) => setFile("certificat_travail", f)} />
            </div>
          </div>

          {/* Sous-groupe 3 : Aptitude médicale / handicap */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#3F3F3F]">
              <Stethoscope className="w-3 h-3 text-[#662483]" /> Aptitude / handicap
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <UploadZone testid="register-visite-medicale" icon={Stethoscope} label="Visite médicale" hint="Données de santé chiffrées — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.visite_medicale} onChange={(f) => setFile("visite_medicale", f)} />
              <UploadZone testid="register-rqth" icon={Accessibility} label="RQTH" hint="Reconnaissance travailleur handicapé — chiffré — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.rqth} onChange={(f) => setFile("rqth", f)} />
            </div>
          </div>

          {/* Sous-groupe 4 : Compétences */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#3F3F3F]">
              <Award className="w-3 h-3 text-[#662483]" /> Compétences et habilitations
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <UploadZone testid="register-caces" icon={Award} label="CACES / habilitations" hint="Certificats CACES, habilitations électriques… — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.caces_habilitations} onChange={(f) => setFile("caces_habilitations", f)} />
              <UploadZone testid="register-other" icon={Paperclip} label="Autres documents" hint="Diplômes, certificats… — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.other} onChange={(f) => setFile("other", f)} />
            </div>
          </div>

          {/* Sous-groupe 5 : Bancaire */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#3F3F3F]">
              <CreditCard className="w-3 h-3 text-[#662483]" /> Bancaire
            </div>
            <UploadZone testid="register-rib" icon={CreditCard} label="RIB" hint="Relevé d'identité bancaire — chiffré — PDF, JPG, PNG" accept=".pdf,.jpg,.jpeg,.png" file={files.rib} onChange={(f) => setFile("rib", f)} />
          </div>
        </div>
      </div>

      {/* Personne à contacter en cas d'urgence — optionnel mais recommandé */}
      <div className="bg-white border border-red-100 p-3 sm:p-4 shadow-sm space-y-3" data-testid="emergency-contact-section">
        <div className="flex items-center gap-2 border-b border-red-100 pb-2">
          <ShieldAlert className="w-4 h-4 text-red-600" />
          <h4 className="text-sm font-bold text-[#3F3F3F]">Personne à contacter en cas d&apos;urgence</h4>
          <span className="text-xs text-gray-400 ml-auto">Optionnel — recommandé</span>
        </div>
        <p className="text-xs text-gray-600">
          Cette personne sera contactée en cas d&apos;accident du travail ou de problème médical grave lors d&apos;une mission.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Nom et prénom</Label>
            <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="ex : Marie DUPONT" className="rounded-none bg-white border-gray-300" data-testid="register-emergency-name" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Téléphone</Label>
            <PhoneInputFr value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} placeholder="06..." inputClassName="bg-white" data-testid="register-emergency-phone" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Lien</Label>
            <select value={form.emergency_contact_relation} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })} className="w-full h-10 rounded-none border border-gray-300 bg-white px-3 text-sm" data-testid="register-emergency-relation">
              <option value="">—</option>
              <option value="conjoint">Conjoint(e)</option>
              <option value="parent">Parent</option>
              <option value="enfant">Enfant</option>
              <option value="fratrie">Frère / Sœur</option>
              <option value="ami">Ami(e)</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>
      </div>
      <div className="bg-[#F0E9F3] border-l-4 border-[#662483] p-3 text-[11px] text-[#3F3F3F] leading-relaxed" data-testid="rgpd-notice">
        <p className="font-semibold text-[#662483] mb-1">Protection de vos données personnelles (RGPD)</p>
        <p>
          Les données et documents que vous fournissez sont collectés par FR INTERIM dans le but exclusif de gérer votre candidature, vous proposer des missions adaptées et remplir nos obligations légales d'employeur (URSSAF, DPAE). Ils sont conservés pendant la durée nécessaire à ces finalités, puis archivés ou supprimés conformément à la loi. Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation et de portabilité, exerçable depuis votre espace personnel ou par email.
        </p>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox checked={form.rgpd_consent} onCheckedChange={(checked) => setForm({ ...form, rgpd_consent: !!checked })} data-testid="register-rgpd-consent" className="mt-0.5" />
        <label className="text-xs text-gray-700 cursor-pointer leading-relaxed" onClick={() => setForm({ ...form, rgpd_consent: !form.rgpd_consent })}>
          J'accepte que FR INTERIM collecte et traite mes données personnelles et documents pour les finalités décrites ci-dessus. <span className="text-red-600">*</span>
        </label>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox checked={form.sms_operational_consent} onCheckedChange={(checked) => setForm({ ...form, sms_operational_consent: !!checked })} data-testid="register-sms-operational-consent" className="mt-0.5" />
        <label className="text-xs text-gray-700 cursor-pointer leading-relaxed" onClick={() => setForm({ ...form, sms_operational_consent: !form.sms_operational_consent })}>
          J'accepte de recevoir des <strong>SMS opérationnels</strong> (confirmation de mission, rappel J-1, horaires).
        </label>
      </div>
      <div className="flex items-start gap-2">
        <Checkbox checked={form.sms_marketing_consent} onCheckedChange={(checked) => setForm({ ...form, sms_marketing_consent: !!checked })} data-testid="register-sms-marketing-consent" className="mt-0.5" />
        <label className="text-xs text-gray-700 cursor-pointer leading-relaxed" onClick={() => setForm({ ...form, sms_marketing_consent: !form.sms_marketing_consent })}>
          J'accepte de recevoir des <strong>SMS commerciaux</strong> (nouvelles offres, relances, événements). Vous pouvez vous désinscrire à tout moment.
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox checked={form.subscribed_offers} onCheckedChange={(checked) => setForm({ ...form, subscribed_offers: !!checked })} data-testid="register-subscribe" />
        <label className="text-sm text-gray-600 cursor-pointer" onClick={() => setForm({ ...form, subscribed_offers: !form.subscribed_offers })}>Je souhaite recevoir les offres par email</label>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold py-3 rounded-none hover:opacity-90" data-testid="register-submit">{loading ? (completeMode ? "Enregistrement..." : "Inscription...") : (completeMode ? "Finaliser mon inscription" : "Créer mon compte")}</Button>
    </form>
  );
}

